import { useCallback, useEffect, useMemo } from 'react';
import { usePageLoading } from './motion/PetalLoader';
import { useNavigate, useParams } from 'react-router-dom';
import PageBackHeader from './ui/PageBackHeader';
import Button from './ui/Button';
import GroupMembersList from './chat/GroupMembersList';
import GroupInfoModals from './chat/GroupInfoModals';
import useGroupInfoPage from '../hooks/useGroupInfoPage';
import { useEventLineup, useMemberAttendanceDays } from '../hooks/queries/useEventLineup';
import { isFestivalEvent } from '../utils/eventType';
import { festivalDaysFromLineup } from '../utils/lineupFormat';
import { goBackTo } from '../lib/navHistory';

import { usePromptAuth } from './auth/AuthPrompt';
export default function GroupMembersPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const promptAuth = usePromptAuth();
  const info = useGroupInfoPage(groupId);
  const pageLoad = usePageLoading(Boolean(info.session) && info.loading, {
    latch: Boolean(info.session),
  });

  // Day dots only exist for festivals, so don't pull a lineup for other groups.
  const eventId = info.event?.event_id;
  const isFestival = isFestivalEvent(info.event);
  const { data: lineup = [] } = useEventLineup(eventId, {
    enabled: Boolean(eventId) && isFestival,
  });
  const festivalDays = useMemo(() => festivalDaysFromLineup(lineup), [lineup]);

  const memberIds = useMemo(
    () => info.members.filter((m) => !m.left_at).map((m) => m.user_id),
    [info.members],
  );
  // Only festival events have days to plot, so skip the query entirely otherwise.
  const { data: attendanceByUser = {} } = useMemberAttendanceDays(eventId, memberIds, {
    enabled: festivalDays.length > 0,
  });

  // replace: true so returning to info doesn't leave this page on the stack for
  // the OS swipe-back gesture to land on.
  const handleBack = useCallback(() => {
    goBackTo(navigate, `/groups/${groupId}/info`);
  }, [navigate, groupId]);

  useEffect(() => {
    if (!info.openActionsMemberId) return undefined;
    const handleOutsideClick = (event) => {
      if (
        info.openActionsRowRef.current &&
        !info.openActionsRowRef.current.contains(event.target)
      ) {
        info.setOpenActionsMemberId(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [info.openActionsMemberId, info.openActionsRowRef, info.setOpenActionsMemberId]);

  if (!info.session) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center">
        <PageBackHeader title="Members" onBack={handleBack} backLabel="Back to group info" />
        <p className="mb-6 text-gray-500 dark:text-dark-muted">Log in to view members.</p>
        <Button size="lg" onClick={() => promptAuth()}>
          Log in
        </Button>
      </div>
    );
  }

  if (pageLoad.show) return pageLoad.node; // only the loader until the page can show whole (Sam, 2026-10-20)

  if (info.error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6">
        <PageBackHeader title="Members" onBack={handleBack} backLabel="Back to group info" />
        <div className="rounded-lg bg-danger-fill p-4 text-danger">{info.error}</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <PageBackHeader title="Members" onBack={handleBack} backLabel="Back to group info" />

      <GroupMembersList
        members={info.members}
        blockedUsers={info.blockedUsers}
        sessionUserId={info.userId}
        festivalDays={festivalDays}
        attendanceByUser={attendanceByUser}
        viewerAttendingDays={attendanceByUser[info.userId] || []}
        isManager={info.isManager}
        isDbUuid={info.isDbUuid}
        canLeaveGroup={info.canLeaveGroup}
        openActionsMemberId={info.openActionsMemberId}
        onOpenActionsMemberIdChange={info.setOpenActionsMemberId}
        openActionsRowRef={info.openActionsRowRef}
        onOpenMemberProfile={info.openMemberProfile}
        onMemberPhotoZoom={info.setMemberPhotoZoom}
        onPromoteMember={info.promoteMemberToManager}
        onRemoveMember={info.setShowRemoveModal}
        onBlockMember={info.setShowBlockModal}
        onUnblockMember={info.handleUnblockUser}
        onLeaveGroup={info.openLeaveGroupModal}
        formatDate={info.formatDate}
      />

      <GroupInfoModals {...info} />
    </div>
  );
}
